import React, { FC } from 'react';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box, Button } from '~Styles';
import { useApi, useButton, useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import useModal from '~ComponentsImproved/Modal/useModal';
import LinkUser from '~Components/LinkUser';
import * as actions from '~actions';
import { GetOrder_order } from '../__queries__';
import styled from '@emotion/styled';
import { ROLE } from '../../../../../__queries__';

const SwitchUserButton = styled(Button)`
  min-height: 32px;
  font-size: 12px;
  padding: 8px 16px;
`;

interface Props {
  order: GetOrder_order;
  /** Called after the order's user has been successfully changed, so the order can be refetched */
  onUserUpdated: () => void;
}

/**
 * Displays the customer info attached to an order. In ES venues, staff can
 * search for and link a different user to the order.
 */
const UserDetails: FC<Props> = ({ order, onUserUpdated }) => {
  const { i18n } = useI18n();
  const logger = useLogger('history-user-details');
  const dispatch = useDispatch();
  const country = useSelector((state) => state.country);
  const linkUserModal = useModal();
  const api = useApi();

  const changeUserButton = useButton({
    onClick: () => {
      logger.info('Opening user search for order', { orderId: order.id });
      linkUserModal.toggle();
    },
  });

  const noUserIsConnected = order.user.role === ROLE.CONTENT_ADMIN || order.user.venues.length > 0;

  if (noUserIsConnected && country !== 'ES') {
    return null;
  }

  return (
    <Box marginBottom="medium">
      <Paragraph level={4} color="var(--colorBlueGray500)" bottom={4}>
        {i18n('History.UserDetails')}
      </Paragraph>

      {!noUserIsConnected && (
        <>
          <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
            {order.user?.name || '-'}
          </Paragraph>
          {order.user?.email && (
            <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
              {order.user?.email || '-'}
            </Paragraph>
          )}
          {order.user?.phone && (
            <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
              {order.user?.phone}
            </Paragraph>
          )}
          {order.user?.address && (
            <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
              {order.user?.address}, {order.user?.zip || ''} {order.user?.city || ''}
            </Paragraph>
          )}
          {order.user?.orgNumber && (
            <Paragraph level={4} color="var(--colorWhite)" bottom={4}>
              {i18n('History.OrgNumber')}: {order.user?.orgNumber}
            </Paragraph>
          )}
        </>
      )}

      {country === 'ES' && (
        <>
          <SwitchUserButton
            {...changeUserButton}
            marginTop={noUserIsConnected ? undefined : 'small'}>
            {noUserIsConnected ? i18n('History.LinkUser') : i18n('History.ChangeUser')}
          </SwitchUserButton>

          {linkUserModal.isShowing && (
            <LinkUser
              isShowing={linkUserModal.isShowing}
              onClose={linkUserModal.toggle}
              isEditingOrder
              onUserSelected={async (userId) => {
                logger.info('Linking new user to order', { orderId: order.id, userId });
                const result = await api.updateOrderUser({ id: order.id, userId });

                if (result.success) {
                  logger.info('Order user updated', { orderId: order.id });
                  onUserUpdated();
                } else {
                  dispatch(actions.addToast(i18n('Error.SomethingWrong'), true, null, true));
                }
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default UserDetails;
